import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Search, Download, Users, ShieldCheck, Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { BrandLogo } from "@/components/ui/brand-logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const fmtDate = (iso) => new Date(iso).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });

export default function Subscribers() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [subs, setSubs] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.get("/admin/subscribers", { headers: { "x-admin-password": password } });
      setSubs(r.data);
      setAuthed(true);
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    window.open(`${API}/admin/subscribers/export?password=${encodeURIComponent(password)}`, "_blank");
  };

  const filtered = subs.filter((s) =>
    `${s.full_name} ${s.email} ${s.country || ""}`.toLowerCase().includes(query.toLowerCase())
  );

  const interested = subs.filter((s) => s.kickstarter_interested).length;

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-5 font-body">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <BrandLogo className="justify-center mb-8" />
          <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
            <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center mx-auto">
              <Lock className="h-6 w-6 text-brand-blue" />
            </div>
            <h1 className="mt-5 font-heading font-bold text-xl text-center">Subscriber Admin</h1>
            <p className="mt-1.5 text-sm text-slate-500 text-center">Enter the admin password to view subscribers.</p>
            <form onSubmit={login} className="mt-6 space-y-3">
              <Input type="password" placeholder="Admin password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 focus-visible:ring-brand-blue" data-testid="admin-password-input" />
              <Button type="submit" disabled={loading} className="w-full bg-brand-blue hover:bg-brand-bluehover h-11" data-testid="admin-login-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa] font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200/60">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <BrandLogo />
          <Badge variant="secondary" className="gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Admin</Badge>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-10">
        <h1 className="font-heading font-bold text-3xl tracking-tight">Subscribers</h1>
        <p className="text-slate-500 mt-1">Everyone who signed up for the ReachMeNow demo and waitlist.</p>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { label: "Total subscribers", value: subs.length, icon: Users, color: "text-brand-blue", bg: "bg-blue-50" },
            { label: "Indiegogo interested", value: interested, icon: ShieldCheck, color: "text-brand-orange", bg: "bg-orange-50" },
            { label: "Consented", value: subs.filter((s) => s.consent).length, icon: Lock, color: "text-brand-blue", bg: "bg-blue-50" },
          ].map((c) => (
            <div key={c.label} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <div className={`h-10 w-10 rounded-lg ${c.bg} flex items-center justify-center`}><c.icon className={`h-5 w-5 ${c.color}`} /></div>
              <p className="mt-3 font-heading font-extrabold text-2xl" data-testid={`stat-${c.label}`}>{c.value}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wide">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <div className="relative max-w-sm w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search by name or email…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 h-11 focus-visible:ring-brand-blue" data-testid="subscriber-search" />
          </div>
          <Button onClick={exportCsv} className="bg-brand-orange hover:bg-brand-orangehover h-11 gap-2" data-testid="export-csv-btn">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>

        <div className="mt-5 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wide">Full Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Country</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Registered</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Consent</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Indiegogo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No subscribers found.</TableCell></TableRow>
              ) : filtered.map((s) => (
                <TableRow key={s.id} data-testid={`subscriber-row-${s.email}`}>
                  <TableCell className="font-semibold">{s.full_name}</TableCell>
                  <TableCell className="text-slate-500">{s.email}</TableCell>
                  <TableCell className="text-slate-500">{s.country || "—"}</TableCell>
                  <TableCell className="text-slate-500">{fmtDate(s.registration_date)}</TableCell>
                  <TableCell>{s.consent ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Yes</Badge> : <Badge variant="secondary">No</Badge>}</TableCell>
                  <TableCell>{s.kickstarter_interested ? <Badge className="bg-orange-100 text-brand-orange hover:bg-orange-100">Yes</Badge> : <Badge variant="secondary">No</Badge>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
